// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext, Parliamentarian } from '../types';
import { partyStyle } from '../parties';
import { fmtDollars, fmtMoney, escapeHtml } from '../format';
import { statCard, seatLabel } from '../ui';

interface RState {
  chamber: string; // parl | House | Senate | other | all
  party: string; // ALL | code
  state: string; // ALL | code
  metric: string; // TOTAL | category name
  current: boolean;
  limit: number; // 25 | 50 | 0(all)
}

const KEY = 'ampx.rankings';
const DEFAULT: RState = { chamber: 'parl', party: 'ALL', state: 'ALL', metric: 'TOTAL', current: false, limit: 50 };

function load(): RState {
  try {
    return { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) || '{}') };
  } catch {
    return { ...DEFAULT };
  }
}
function save(s: RState): void {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

function chamberPass(p: Parliamentarian, c: string): boolean {
  if (c === 'all') return true;
  if (c === 'parl') return p.chamber === 'House' || p.chamber === 'Senate';
  if (c === 'other') return p.chamber === 'Former PM' || p.chamber === 'Other';
  return p.chamber === c;
}

function metricValue(p: Parliamentarian, metric: string): number {
  return metric === 'TOTAL' ? p.total : p.byCategory[metric] || 0;
}

export function renderRankings(root: HTMLElement, ctx: AppContext): void {
  const { data } = ctx;
  const s = load();

  const categories = data.summary.categories.map((c) => c.name);
  const partyOptions = data.summary.parties;
  const stateOptions = data.summary.states.map((x) => x.code);

  root.innerHTML = `
    <div class="view-head">
      <h2>Who spends the most</h2>
      <p>Every federal parliamentarian ranked by their total taxpayer-funded work expenses since the 2022 election. Filter by chamber, party or state, or rank by a single expense category. Click anyone to see their full breakdown.</p>
    </div>
    <div class="stat-row" id="rk-stats"></div>
    <div class="filters" id="rk-filters">
      <div class="filter"><label for="f-chamber">Chamber</label>
        <select id="f-chamber">
          <option value="parl">MPs &amp; Senators</option>
          <option value="House">House only</option>
          <option value="Senate">Senate only</option>
          <option value="other">Former PMs &amp; others</option>
          <option value="all">Everyone</option>
        </select></div>
      <div class="filter"><label for="f-party">Party</label>
        <select id="f-party"><option value="ALL">All parties</option>${partyOptions
          .map((p) => `<option value="${p.code}">${escapeHtml(p.name)} (${p.members})</option>`)
          .join('')}</select></div>
      <div class="filter"><label for="f-state">State / territory</label>
        <select id="f-state"><option value="ALL">All</option>${stateOptions
          .map((c) => `<option value="${c}">${c}</option>`)
          .join('')}</select></div>
      <div class="filter"><label for="f-metric">Rank by</label>
        <select id="f-metric"><option value="TOTAL">Total expenses</option>${categories
          .map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`)
          .join('')}</select></div>
      <div class="filter"><label for="f-limit">Show</label>
        <select id="f-limit"><option value="25">Top 25</option><option value="50">Top 50</option><option value="0">All</option></select></div>
      <label class="check"><input type="checkbox" id="f-current" /> Current members only</label>
    </div>
    <div class="panel panel-pad"><div class="bar-list" id="rk-list"></div></div>`;

  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
  ($('f-chamber') as HTMLSelectElement).value = s.chamber;
  ($('f-party') as HTMLSelectElement).value = s.party;
  ($('f-state') as HTMLSelectElement).value = s.state;
  ($('f-metric') as HTMLSelectElement).value = s.metric;
  ($('f-limit') as HTMLSelectElement).value = String(s.limit);
  ($('f-current') as HTMLInputElement).checked = s.current;

  const draw = () => {
    save(s);
    let list = data.people.filter(
      (p) =>
        chamberPass(p, s.chamber) &&
        (s.party === 'ALL' || p.partyCode === s.party) &&
        (s.state === 'ALL' || p.state === s.state) &&
        (!s.current || p.isCurrent)
    );
    list = list.filter((p) => metricValue(p, s.metric) > 0);
    list.sort((a, b) => metricValue(b, s.metric) - metricValue(a, s.metric));
    const shown = s.limit > 0 ? list.slice(0, s.limit) : list;
    const max = shown.length ? metricValue(shown[0], s.metric) : 1;

    // stats
    const sum = list.reduce((t, p) => t + metricValue(p, s.metric), 0);
    const totals = list.map((p) => metricValue(p, s.metric)).sort((a, b) => a - b);
    const med = totals.length ? totals[Math.floor(totals.length / 2)] : 0;
    const metricLabel = s.metric === 'TOTAL' ? 'total expenses' : s.metric.toLowerCase();
    $('rk-stats').innerHTML =
      statCard('Parliamentarians', String(list.length), `matching filters`) +
      statCard(s.metric === 'TOTAL' ? 'Combined total' : `Combined ${s.metric}`, fmtDollars(sum)) +
      statCard('Top spender', shown.length ? fmtDollars(metricValue(shown[0], s.metric)) : '—', shown.length ? escapeHtml(shown[0].name) : '') +
      statCard('Median', fmtMoney(med), `per person, ${escapeHtml(metricLabel)}`);

    if (!shown.length) {
      $('rk-list').innerHTML = '<div class="state-msg">No parliamentarians match these filters.</div>';
      return;
    }
    $('rk-list').innerHTML = shown
      .map((p, i) => {
        const v = metricValue(p, s.metric);
        const w = max > 0 ? (v / max) * 100 : 0;
        const col = partyStyle(p.partyCode).colour;
        const tip = `${escapeHtml(p.name)} — ${escapeHtml(partyStyle(p.partyCode).name)}\n${escapeHtml(seatLabelText(p))}\n${escapeHtml(s.metric === 'TOTAL' ? 'Total' : s.metric)}: ${fmtDollars(v)}`;
        return `<div class="bar-row" role="button" tabindex="0" data-slug="${p.slug}" data-tip="${tip}" aria-label="${escapeHtml(p.name)}, ${fmtDollars(v)}">
          <div class="bar-rank">${i + 1}</div>
          <div class="bar-main">
            <div class="bar-label">
              <span class="bar-name" style="border-left:3px solid ${col};padding-left:7px">${escapeHtml(p.name)}</span>
              <span class="bar-meta">${escapeHtml(partyStyle(p.partyCode).code)} · ${escapeHtml(seatLabel(p))}</span>
              <span class="bar-value">${fmtDollars(v)}</span>
            </div>
            <div class="bar-track"><div class="bar-fill" style="width:${w.toFixed(2)}%;background:${col}"></div></div>
          </div>
        </div>`;
      })
      .join('');
  };

  root.querySelector('#rk-list')!.addEventListener('click', (e) => {
    const row = (e.target as Element).closest<HTMLElement>('.bar-row');
    if (row?.dataset.slug) ctx.openMp(row.dataset.slug);
  });
  root.querySelector('#rk-list')!.addEventListener('keydown', (e) => {
    const ke = e as KeyboardEvent;
    if (ke.key !== 'Enter' && ke.key !== ' ') return;
    const row = (ke.target as Element).closest<HTMLElement>('.bar-row');
    if (row?.dataset.slug) { ke.preventDefault(); ctx.openMp(row.dataset.slug); }
  });

  $('f-chamber').addEventListener('change', (e) => { s.chamber = (e.target as HTMLSelectElement).value; draw(); });
  $('f-party').addEventListener('change', (e) => { s.party = (e.target as HTMLSelectElement).value; draw(); });
  $('f-state').addEventListener('change', (e) => { s.state = (e.target as HTMLSelectElement).value; draw(); });
  $('f-metric').addEventListener('change', (e) => { s.metric = (e.target as HTMLSelectElement).value; draw(); });
  $('f-limit').addEventListener('change', (e) => { s.limit = Number((e.target as HTMLSelectElement).value); draw(); });
  $('f-current').addEventListener('change', (e) => { s.current = (e.target as HTMLInputElement).checked; draw(); });

  draw();
}

function seatLabelText(p: Parliamentarian): string {
  if (p.chamber === 'Senate') return `Senator, ${p.state ?? ''}`;
  if (p.electorate) return `Member for ${p.electorate.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase())}`;
  return p.role ?? '';
}
